<template>
<div class="payment-result-container">
  <div class="row justify-content-center">
    <div class="col-md-8 col-lg-6">
      <div class="card payment-card success-card">
        
        <!-- Header Banner with Premium Styling and Check Animation -->
        <div class="card-header-banner">
          <div class="success-icon-wrapper">
            <svg class="checkmark-svg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
              <circle class="checkmark-circle" cx="26" cy="26" r="25" fill="none"/>
              <path class="checkmark-check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8"/>
            </svg>
          </div>
          <h2 class="title-primary">{{ $t("message.payment_success") }}</h2>
          <p class="subtitle-primary">{{ $t("message.payment_success_msg") }}</p>
        </div>

        <div class="card-body p-4">
          <!-- Loading State -->
          <div v-if="loading" class="text-center py-4">
            <div class="spinner-border text-success" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-2 text-muted">Retrieving payment details...</p>
          </div>

          <!-- Transaction details -->
          <div v-else-if="currentInvoice && currentInvoice.transaction_id" class="invoice-summary-details">
            <h5 class="section-title">
              <i class="fas fa-receipt me-2"></i>{{ $t("message.transaction_details") }}
            </h5>
            
            <div class="details-grid">
              <div class="detail-row">
                <span class="detail-label">{{ $t("message.invoice_number") }}</span>
                <span class="detail-value text-highlight">{{ currentInvoice.invoice_number }}</span>
              </div>
              <div class="detail-row" v-if="currentInvoice.transaction_id">
                <span class="detail-label">{{ $t("message.transaction_id") }}</span>
                <span class="detail-value text-muted-value">{{ currentInvoice.transaction_id }}</span>
              </div>
              <div class="detail-row" v-if="currentInvoice.paid_at">
                <span class="detail-label">{{ $t("message.paid_date") }}</span>
                <span class="detail-value text-muted-value">{{ currentInvoice.paid_at }}</span>
              </div>
              <div class="detail-row" v-if="currentInvoice.payment_method">
                <span class="detail-label">{{ $t("message.payment_option") }}</span>
                <span class="detail-value">
                  <span class="payment-method-badge">
                    <i class="fas fa-wallet me-1"></i>
                    {{ formatPaymentMethod(currentInvoice.payment_method) }}
                  </span>
                </span>
              </div>
              <div class="detail-row">
                <span class="detail-label">{{ $t("message.payment_status") }}</span>
                <span class="detail-value">
                  <span class="status-badge success-badge">
                    <i class="fas fa-check-circle me-1"></i>
                    {{ $t("message.paid") }}
                  </span>
                </span>
              </div>
            </div>

            <!-- Items List -->
            <div class="items-list-container mt-4" v-if="currentInvoice.items && currentInvoice.items.length">
              <h5 class="section-title">
                <i class="fas fa-users me-2"></i>Registered Participants
              </h5>
              
              <div class="invoice-items">
                <div v-for="(item, idx) in currentInvoice.items" :key="idx" class="invoice-item-card">
                  <div class="item-icon">
                    <i class="fas fa-user-check"></i>
                  </div>
                  <div class="item-info">
                    <div class="item-name" v-if="item.registration_item && item.registration_item.participant">
                      {{ item.registration_item.participant.lastname_en }} {{ item.registration_item.participant.firstname_en }}
                    </div>
                    <div class="item-sub">
                      <span class="badge badge-apply-for" :class="item.registration_item.apply_for == 1 ? 'badge-new' : 'badge-cpe'">
                        {{ item.registration_item.apply_for == 1 ? $t("message.new") : "CPE" }}
                      </span>
                      <!-- <span class="business-type-text ms-2">
                        {{ getBusinessType(item.registration_item.type_business) }}
                      </span> -->
                    </div>
                  </div>
                  <div class="item-amount">
                    {{ fomartAmount(item.amount) }} {{ item.currency || 'KHR' }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Total amount breakdown -->
            <div class="price-summary mt-4">
              <div class="price-row total-amount-row">
                <span>{{ $t("message.total_amount") }}</span>
                <span class="price-val">{{ fomartAmount(currentInvoice.total_amount) }} {{ currentInvoice.currency || 'KHR' }}</span>
              </div>
            </div>
          </div>

          <!-- Fallback when no details could be retrieved -->
          <div v-else class="alert alert-info text-center py-4 my-3">
            <i class="fas fa-info-circle fa-2x mb-3 text-info"></i>
            <h5>{{ $t("message.payment_success_desc") }}</h5>
            <p class="text-muted mb-0">Your payment has been successfully recorded in our system. You may now return to your dashboard.</p>
          </div>

          <!-- Actions -->
          <div class="action-buttons-group mt-4">
            <router-link 
              v-if="currentInvoice && currentInvoice.transaction_id"
              :to="{ name: 'printInvoice', params: { transactionId: currentInvoice.transaction_id } }" 
              class="btn btn-print-invoice"
            >
              <i class="fas fa-print me-2"></i>{{ $t("message.view_invoice") }}
            </router-link>
            
            <router-link :to="{ name: 'Dashboard' }" class="btn btn-to-dashboard">
              <i class="fas fa-home me-2"></i>{{ $t("message.back_to_dashboard") }}
            </router-link>
          </div>
        </div>

      </div>
    </div>
  </div>
</div>
</template>

<script>
import axios from "@/axios";
import { mapActions } from "vuex";

export default {
  name: 'PaymentSuccess',
  data() {
    return {
      loading: false,
      currentInvoice: {},
      transactionId: null,
    };
  },
  methods: {
    ...mapActions([
      'act_shopcard',
    ]),
    showLoader() {
      return this.$loading.show({
        color: "#01B201",
        canCancel: false
      });
    },
    async checkAcledaPayment() {
      if (!this.transactionId) return;
      try {
        const response = await axios.post(
          "check-acleda-payment",
          { transactionID: this.transactionId },
          {
            headers: {
              Authorization: "Bearer " + this.$store.getters.getAuthToken
            }
          }
        );
        if (response.status === 200 && response.data && response.data.data) {
          this.currentInvoice = response.data.data;
        }
      } catch (error) {
        console.error("Error checking ACLEDA payment status:", error);
      }
    },
    async getInvoiceDetail() {
      if (!this.transactionId) return;
      try {
        const response = await axios.get("get-detail-payment-history/" + this.transactionId, {
          headers: {
            Authorization: "Bearer " + this.$store.getters.getAuthToken
          }
        });
        if (response.status === 200 && response.data.data) {
          this.currentInvoice = response.data.data;
        }
      } catch (error) {
        console.error("Error fetching invoice details:", error);
      }
    },
    async initPaymentSuccess() {
      if (!this.transactionId) return;
      this.loading = true;
      try {
        await this.checkAcledaPayment();
        await this.getInvoiceDetail();

        // If invoice is not found or status is not paid (status != 1), redirect to PaymentFailed
        if (
          !this.currentInvoice ||
          !this.currentInvoice.transaction_id ||
          (this.currentInvoice.status != 1 && this.currentInvoice.status != '1')
        ) {
          this.$router.push({
            name: 'paymentFailed',
            params: { transactionId: this.transactionId }
          });
          return;
        }

        if (this.act_shopcard) {
          await this.act_shopcard();
        }
      } catch (error) {
        console.error("Error initializing payment success:", error);
      } finally {
        this.loading = false;
      }
    },
    formatPaymentMethod(method) {
      if (!method) return '';
      switch (method.toLowerCase()) {
        case 'abapay':
          return 'ABA PAY';
        case 'bakong':
          return 'KHQR (Bakong)';
        case 'cards':
          return 'Credit/Debit Card';
        case 'acleda_bakong':
          return 'ACLEDA KHQR';
        case 'acleda_cards':
          return 'ACLEDA Card';
        default:
          return method.toUpperCase();
      }
    },
    fomartAmount(amount) {
      if (amount === undefined || amount === null) return '0';
      return Number(Number(amount).toFixed(1)).toLocaleString();
    },
    getBusinessType(t) {
      switch (t) {
        case '1':
        case 1:
          return this.$t("message.stock_business");
        case '2':
        case 2:
          return this.$t("message.derivative_business");
        case '19':
        case 19:
          return this.$t("message.public") + ' - ' + this.$t("message.stock_business");
        case 20:
        case '20':
          return this.$t("message.public") + ' - ' + this.$t("message.derivative_business");
        default:
          return t;
      }
    }
  },
  created() {
    // Get transaction ID from route parameters or query parameters
    this.transactionId = this.$route.params.transactionId || this.$route.query.transaction_id || this.$route.query.transactionID;
    if (this.transactionId) {
      this.initPaymentSuccess();
    }
  }
};
</script>

<style scoped>
.payment-result-container {
  padding: 60px 15px;
  background-color: #f8f9fa;
  min-height: 80vh;
}

.payment-card {
  border: none;
  border-radius: 20px;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  background: #ffffff;
  transition: all 0.3s ease;
}

/* Success Header Stylings */
.success-card .card-header-banner {
  background: linear-gradient(135deg, #0e8937 0%, #15aa47 100%);
  padding: 40px 20px;
  text-align: center;
  color: #ffffff;
}

.title-primary {
  font-size: 1.8rem;
  font-weight: 800;
  margin-top: 15px;
  margin-bottom: 8px;
  letter-spacing: 0.5px;
}

.subtitle-primary {
  font-size: 1rem;
  opacity: 0.9;
  margin-bottom: 0;
  max-width: 90%;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.5;
}

/* Animated Checkmark SVG */
.success-icon-wrapper {
  width: 80px;
  height: 80px;
  margin: 0 auto;
}

.checkmark-svg {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: block;
  stroke-width: 3;
  stroke: #ffffff;
  stroke-miterlimit: 10;
  box-shadow: inset 0px 0px 0px #15aa47;
  animation: fillAnimation .4s ease-in-out .4s forwards, scaleAnimation .3s ease-in-out .9s toggle;
}

.checkmark-circle {
  stroke-dasharray: 166;
  stroke-dashoffset: 166;
  stroke-width: 3;
  stroke: rgba(255, 255, 255, 0.3);
  fill: none;
  animation: strokeAnimation 0.6s cubic-bezier(0.65, 0, 0.45, 1) forwards;
}

.checkmark-check {
  transform-origin: 50% 50%;
  stroke-dasharray: 48;
  stroke-dashoffset: 48;
  animation: strokeAnimation 0.3s cubic-bezier(0.65, 0, 0.45, 1) 0.8s forwards;
}

@keyframes strokeAnimation {
  100% {
    stroke-dashoffset: 0;
  }
}

@keyframes scaleAnimation {
  0%, 100% {
    transform: none;
  }
  50% {
    transform: scale3d(1.1, 1.1, 1);
  }
}

@keyframes fillAnimation {
  100% {
    box-shadow: inset 0px 0px 0px 40px rgba(255, 255, 255, 0.15);
  }
}

/* Section Title */
.section-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #2b2b2b;
  margin-bottom: 16px;
  border-bottom: 2px solid #f1f3f5;
  padding-bottom: 8px;
}

.section-title i {
  color: #15aa47;
}

/* Details Grid */
.details-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
  background-color: #f8f9fa;
  padding: 20px;
  border-radius: 12px;
  border: 1px solid #e9ecef;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.95rem;
}

.detail-label {
  color: #6c757d;
  font-weight: 500;
}

.detail-value {
  font-weight: 600;
  color: #2b2b2b;
}

.text-highlight {
  color: #0e8937;
  font-weight: 700;
}

.text-muted-value {
  color: #495057;
}

/* Badges */
.payment-method-badge {
  background-color: rgba(0, 90, 156, 0.1);
  color: #005a9c;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.82rem;
  font-weight: 700;
  text-transform: uppercase;
}

.status-badge {
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.82rem;
  font-weight: 700;
  text-transform: uppercase;
}

.success-badge {
  background-color: rgba(14, 137, 55, 0.1);
  color: #0e8937;
}

/* Invoice Items */
.invoice-items {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.invoice-item-card {
  display: flex;
  align-items: center;
  padding: 14px;
  border: 1px solid #f1f3f5;
  border-radius: 12px;
  background-color: #ffffff;
  transition: all 0.2s ease;
}

.invoice-item-card:hover {
  border-color: #15aa47;
  transform: translateY(-1px);
}

.item-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: rgba(14, 137, 55, 0.08);
  color: #0e8937;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  flex-shrink: 0;
}

.item-info {
  flex-grow: 1;
}

.item-name {
  font-weight: 700;
  font-size: 0.95rem;
  color: #2b2b2b;
}

.item-sub {
  display: flex;
  align-items: center;
  margin-top: 4px;
}

.badge-apply-for {
  font-size: 0.7rem;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 10px;
  text-transform: uppercase;
}

.badge-new {
  background-color: rgba(14, 137, 55, 0.1);
  color: #0e8937;
}

.badge-cpe {
  background-color: rgba(0, 90, 156, 0.1);
  color: #005a9c;
}

.business-type-text {
  font-size: 0.75rem;
  color: #6c757d;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
}

.item-amount {
  font-weight: 700;
  color: #2b2b2b;
  font-size: 0.95rem;
}

/* Total Amount breakdown styling */
.price-summary {
  background-color: #f8f9fa;
  border-radius: 12px;
  padding: 16px 20px;
  border: 1px solid #e9ecef;
}

.price-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.total-amount-row {
  font-weight: 800;
  font-size: 1.1rem;
  color: #2b2b2b;
}

.total-amount-row .price-val {
  color: #0e8937;
  font-size: 1.25rem;
}

/* Action Buttons Group */
.action-buttons-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.btn-print-invoice {
  background: linear-gradient(135deg, #005a9c 0%, #0077cc 100%);
  color: #ffffff;
  border: none;
  font-weight: 700;
  padding: 12px 20px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 90, 156, 0.2);
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
}

.btn-print-invoice:hover {
  background: linear-gradient(135deg, #004070 0%, #005a9c 100%);
  transform: translateY(-1px);
  color: #ffffff;
}

.btn-to-dashboard {
  background-color: #ffffff;
  border: 2px solid #e9ecef;
  color: #495057;
  font-weight: 700;
  padding: 12px 20px;
  border-radius: 12px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
}

.btn-to-dashboard:hover {
  background-color: #f8f9fa;
  border-color: #ced4da;
  color: #212529;
}

@media (min-width: 576px) {
  .action-buttons-group {
    flex-direction: row;
  }
  .btn-print-invoice {
    flex: 1;
  }
  .btn-to-dashboard {
    flex: 1;
  }
}
</style>
